import React, { useState, useEffect, useRef } from "react";
import {
  FiPlus,
  FiMinus,
  FiDownload,
  FiEdit,
  FiTrash2,
  FiUser,
  FiArrowLeft,
  FiPhone,
  FiMail,
} from "react-icons/fi";
import html2canvas from "html2canvas";
import { motion, AnimatePresence } from "framer-motion";
import { getAllDocsSnap } from "../../../utils/firebase/Firebase.utils";
import toast from "react-hot-toast";
import Modal from "../Components/Modal";
import AddContactForm from "./AddContactForm";
import { Link } from "react-router-dom";
import { hoaLabel } from "../../../utils/constants";

const OrgChartNode = ({ node, onEdit, onDelete, onAddChild }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="flex flex-col items-center">
      <div className="relative group">
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4 w-52 mx-auto mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-eleghoBlue/10 rounded-full flex items-center justify-center flex-shrink-0">
              {node.photoUrl ? (
                <img
                  src={node.photoUrl}
                  alt={node.name}
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <FiUser className="text-eleghoBlue" />
              )}
            </div>
            <div className="overflow-hidden">
              <h3 className="font-medium text-sm text-eleghoBlue truncate">
                {node.name}
              </h3>
              <p className="text-gray-600 text-xs truncate">{node.title}</p>
            </div>
          </div>

          <div className="absolute -right-1 -top-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
            <button
              onClick={() => onEdit(node)}
              className="p-1 bg-white rounded-full shadow-md text-eleghoBlue hover:bg-eleghoBlue hover:text-white transition-colors"
            >
              <FiEdit className="w-3 h-3" />
            </button>
            <button
              onClick={() => onDelete(node)}
              className="p-1 bg-white rounded-full shadow-md text-red-500 hover:bg-red-500 hover:text-white transition-colors"
            >
              <FiTrash2 className="w-3 h-3" />
            </button>
            <button
              onClick={() => onAddChild(node)}
              className="p-1 bg-white rounded-full shadow-md text-green-500 hover:bg-green-500 hover:text-white transition-colors"
            >
              <FiPlus className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {node.children && node.children.length > 0 && (
        <>
          <div className="w-px h-4 bg-gray-300"></div>
          <div className="relative">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-10 w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center border border-gray-300 text-gray-600 hover:bg-gray-200"
            >
              {isExpanded ? (
                <FiMinus className="w-3 h-3" />
              ) : (
                <FiPlus className="w-3 h-3" />
              )}
            </button>
            {isExpanded && (
              <>
                <div className="h-6 w-px bg-gray-300 mx-auto"></div>
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="grid grid-flow-col gap-4 relative"
                  >
                    {node.children.map((child, index) => (
                      <div key={child.id || index} className="flex flex-col">
                        <OrgChartNode
                          node={child}
                          onEdit={onEdit}
                          onDelete={onDelete}
                          onAddChild={onAddChild}
                        />
                      </div>
                    ))}
                    {node.children.length > 1 && (
                      <div className="absolute top-0 left-0 right-0 h-px bg-gray-300"></div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default function OrgChart() {
  const [contacts, setContacts] = useState([]);
  const [orgChartData, setOrgChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [addChildMode, setAddChildMode] = useState(false);
  const [parentContact, setParentContact] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const chartRef = useRef(null);
  const [statsData, setStatsData] = useState({
    totalContacts: 0,
    directoryContacts: 0,
    emergencyContacts: 0,
  });

  useEffect(() => {
    const fetchContacts = async () => {
      await getAllDocsSnap("contacts", (data) => {
        // Filter contacts to only include "Condo Corp Directory" category
        const condoCorpContacts = data.filter(
          (contact) => contact.category === "Condo Corp Directory"
        );
        setContacts(condoCorpContacts);
        setLoading(false);

        // Set stats data
        setStatsData({
          totalContacts: condoCorpContacts.length,
          directoryContacts: data.filter(
            (c) => c.category ===  `${hoaLabel} Directory`
          ).length,
          emergencyContacts: data.filter(
            (c) => c.category === "Emergency Hotlines"
          ).length,
        });
      });
    };
    fetchContacts();
  }, []);

  useEffect(() => {
    if (contacts.length > 0) {
      buildOrgChart();
    }
  }, [contacts]);

  const buildOrgChart = () => {
    // Map contacts to have a parent-child relationship
    const contactsMap = contacts.reduce((acc, contact) => {
      if (!contact.id) return acc;
      acc[contact.id] = { ...contact, children: [] };
      return acc;
    }, {});

    // Connect parents and children
    const rootNodes = [];

    contacts.forEach((contact) => {
      if (contact.reportsTo) {
        // If contact has a reportTo, add it as a child to that parent
        if (contactsMap[contact.reportsTo]) {
          contactsMap[contact.reportsTo].children.push(contactsMap[contact.id]);
        } else {
          // If parent doesn't exist, add to root nodes
          rootNodes.push(contactsMap[contact.id]);
        }
      } else {
        // If no reportTo, it's a root level contact
        rootNodes.push(contactsMap[contact.id]);
      }
    });

    setOrgChartData(rootNodes);
  };

  const handleEdit = (contact) => {
    setSelectedContact(contact);
    setEditMode(true);
  };

  const handleDelete = (contact) => {
    if (
      window.confirm(
        "Are you sure you want to delete this contact? This will also remove all subordinates from the org chart."
      )
    ) {
      // In a real implementation, you would delete the contact from Firebase
      // and handle the reorganization of reporting structure
      toast.success("Contact deleted from org chart");
      buildOrgChart();
    }
  };

  const handleAddChild = (parent) => {
    setParentContact(parent);
    setAddChildMode(true);
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.1, 0.5));
  };

  const downloadOrgChart = () => {
    if (!chartRef.current) return;

    html2canvas(chartRef.current).then((canvas) => {
      const link = document.createElement("a");
      link.download = "org-chart.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">Loading...</div>
    );
  }

  return (
    <div className="text-xs max-w-7xl mx-auto px-4 sm:px-6">
      <div className="title-header mt-4 text-lg sm:text-xl font-semibold">
        Contact Management
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 my-4 sm:my-6">
        <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-eleghoBlue/10 rounded-lg">
              <FiPhone className="w-4 h-4 sm:w-5 sm:h-5 text-eleghoBlue" />
            </div>
            <div>
              <div className="text-xs sm:text-sm text-gray-600">
                Total Contacts
              </div>
              <div className="text-xl sm:text-2xl font-semibold text-eleghoBlue">
                {statsData.totalContacts}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-eleghoGreen/10 rounded-lg">
              <FiPhone className="w-4 h-4 sm:w-5 sm:h-5 text-eleghoGreen" />
            </div>
            <div>
              <div className="text-xs sm:text-sm text-gray-600">
                Emergency Contacts
              </div>
              <div className="text-xl sm:text-2xl font-semibold text-eleghoGreen">
                {statsData.emergencyContacts}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-50 rounded-lg">
              <FiMail className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
            </div>
            <div>
              <div className="text-xs sm:text-sm text-gray-600">
                Directory Contacts
              </div>
              <div className="text-xl sm:text-2xl font-semibold text-gray-600">
                {statsData.directoryContacts}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6 pt-4">
        <Link
          to="/contacts"
          className="flex items-center gap-2 text-eleghoBlue hover:text-eleghoBlue/80"
        >
          <FiArrowLeft className="w-4 h-4" />
          <span>Back to Contacts</span>
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 sm:gap-4 mb-6">
        <button className="px-3 sm:px-4 py-2 rounded-lg text-xs transition-colors bg-eleghoBlue text-white cursor-default">
          Condo Corp Directory
        </button>
        <Link
          to="/contacts"
          className="px-3 sm:px-4 py-2 rounded-lg text-xs transition-colors bg-white text-gray-600 hover:bg-gray-50"
        >
          Emergency Hotlines
        </Link>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">
          Condo Corp Directory Organization Chart
        </h2>

        <div className="flex items-center gap-2">
          <button
            onClick={handleZoomOut}
            className="p-2 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            <FiMinus className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600">
            {Math.round(zoomLevel * 100)}%
          </span>
          <button
            onClick={handleZoomIn}
            className="p-2 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            <FiPlus className="w-4 h-4" />
          </button>
          {/* <button
            onClick={downloadOrgChart}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 text-sm"
          >
            <FiDownload className="w-4 h-4" />
            Download
          </button> */}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 overflow-auto">
        <div
          ref={chartRef}
          className="min-w-full overflow-x-auto p-8 flex justify-center"
          style={{
            transform: `scale(${zoomLevel})`,
            transformOrigin: "top center",
          }}
        >
          {orgChartData && orgChartData.length > 0 ? (
            <div className="flex flex-col">
              {orgChartData.map((node, index) => (
                <div key={node.id || index} className="mb-8">
                  <OrgChartNode
                    node={node}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAddChild={handleAddChild}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <p className="text-gray-500">
                No organization structure found for Condo Corp Directory. Add
                contacts with reporting relationships to build the org chart.
              </p>
            </div>
          )}
        </div>
      </div>

      {editMode && (
        <Modal>
          <AddContactForm
            data={selectedContact}
            setEditMode={setEditMode}
            editMode={editMode}
            setAddMode={() => {}}
          />
        </Modal>
      )}

      {addChildMode && (
        <Modal>
          <div>
            <AddContactForm
              reportsTo={parentContact?.id}
              setAddMode={setAddChildMode}
              setEditMode={() => {}}
              editMode={editMode}
              defaultCategory="Condo Corp Directory"
            />
          </div>
        </Modal>
      )}
    </div>
  );
}
